import React, {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import analytics from '@sy/app-client/src/ssrAdapter/services/analytics';
import {
	MarketingHighlight,
	MarketingTestimonialCarousel,
} from '@sy/app-client/src/ssrAdapter/components/Marketing';
import { PricingBrandsBanner } from '@sy/app-client/src/ssrAdapter/components/Pricing';
import { mixins } from '@sy/component-library';
import { CacheReducedCaseStudy } from '@sy/ssr-types';
import Link from '~/components/SsrLink';
import { events } from '~/services/tracking';
import type { TrackProperties } from '~/services/tracking/types';
import { CaseStudyTag, allTagLinks } from '~/services/caseStudy/caseStudyTags';
import ReducedCaseStudy from '~/scenes/CaseStudies/scenes/components/ReducedCaseStudy';
import { TagLink, TagList } from '~/scenes/CaseStudies/scenes/components/Tags';
import { useMediaQuery } from '~/utils/hooks';
import { REDUCED_CASE_STUDY_IMAGE_SIZES } from './constants';
import {
	BackgroundWrap,
	Content,
	LoadTrigger,
	NoPosts,
	PostGrid,
	Subtitle,
	Title,
	Wrap,
} from './CaseStudiesOverview.styles';

interface CaseStudiesOverviewProps {
	caseStudies: CacheReducedCaseStudy[] | null;
	tag?: CaseStudyTag | null;
}

const { mediaQueries } = mixins;

const INITIAL_LOADED_POSTS_COUNT = 15;
const ROWS_TO_LOAD_COUNT = 5;

const CaseStudiesOverview = ({
	caseStudies = [],
	tag,
}: CaseStudiesOverviewProps) => {
	const [loaded, setLoaded] = useState<number>(
		Math.min(caseStudies?.length || 0, INITIAL_LOADED_POSTS_COUNT),
	);
	const [hasMorePosts, setHasMorePosts] = useState<boolean>(
		(caseStudies?.length || 0) > INITIAL_LOADED_POSTS_COUNT,
	);
	const [loadMoreCount, setLoadMoreCount] = useState<number>(0);

	const { t } = useTranslation('caseStudies');

	const isLarge = useMediaQuery(mediaQueries.large);
	const isMedium = useMediaQuery(mediaQueries.medium);

	const loadCount = useMemo(() => {
		if (isLarge) return 3 * ROWS_TO_LOAD_COUNT;
		if (isMedium) return 2 * ROWS_TO_LOAD_COUNT;
		return ROWS_TO_LOAD_COUNT;
	}, [isLarge, isMedium]);

	const loadTriggerRef = useRef<HTMLDivElement>(null);

	const trackContentClick = useCallback((properties: TrackProperties) => {
		analytics.track(events.CONTENT_CLICKED, properties);
	}, []);

	const trackLoadMore = useCallback((count: number) => {
		analytics.track(events.CASE_STUDIES_LOAD_MORE, { count });
	}, []);

	const loadMore = useCallback(() => {
		if (!caseStudies || !hasMorePosts) {
			return;
		}

		const newLoadMoreCount = loadMoreCount + 1;
		setLoadMoreCount(newLoadMoreCount);
		trackLoadMore(newLoadMoreCount);

		setLoaded(currentlyLoaded => {
			if (caseStudies && caseStudies.length <= currentlyLoaded + loadCount) {
				setHasMorePosts(false);
			}
			return currentlyLoaded + loadCount;
		});
	}, [
		loadCount,
		loadMoreCount,
		hasMorePosts,
		caseStudies,
		setHasMorePosts,
		trackLoadMore,
	]);

	useEffect(() => {
		if (!loadTriggerRef.current) {
			return () => {};
		}

		const ref = loadTriggerRef.current;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					loadMore();
				}
			},
			{
				root: null,
				threshold: 0.1,
			},
		);

		observer.observe(ref);
		return () => observer.unobserve(ref);
	}, [loadMore]);

	useEffect(() => {
		analytics.page(tag || 'All', 'Case Studies', {
			contentType: 'Case Studies',
			pageType: 'Tag',
			pageTag: tag || 'All',
		});
	}, [tag]);

	useEffect(() => {
		setLoaded(Math.min(caseStudies?.length || 0, INITIAL_LOADED_POSTS_COUNT));
		setHasMorePosts((caseStudies?.length || 0) > INITIAL_LOADED_POSTS_COUNT);
		setLoadMoreCount(0);
	}, [caseStudies]);

	return (
		<Fragment>
			<main role="main">
				<BackgroundWrap>
					<Wrap>
						<Title>{t('caseStudies.hero.title')}</Title>
						<Subtitle>{t('caseStudies.hero.subTitle')}</Subtitle>
					</Wrap>
				</BackgroundWrap>

				<Content>
					<TagList>
						{allTagLinks.map(({ tag: existingTag, translationKey }) => (
							<Link
								key={existingTag}
								href={
									existingTag
										? `/case-studies?tag=${existingTag}`
										: '/case-studies'
								}
								passHref
							>
								<TagLink highlight={existingTag === tag}>
									{t(translationKey)}
								</TagLink>
							</Link>
						))}
					</TagList>
					<PostGrid>
						{(caseStudies || []).slice(0, loaded)?.map((caseStudy, index) => (
							<ReducedCaseStudy
								imageSizes={REDUCED_CASE_STUDY_IMAGE_SIZES}
								key={caseStudy.id}
								caseStudy={caseStudy}
								onClick={() =>
									trackContentClick({
										destinationContentName: caseStudy.title,
										contentType: 'caseStudy',
										destinationUrl: caseStudy.urlPath,
										location: 'main feed',
										position: index + 1,
									})
								}
							/>
						))}
					</PostGrid>
				</Content>
				{!hasMorePosts && caseStudies?.length && caseStudies?.length > 6 && (
					<NoPosts>{t('caseStudies.overview.noMorePosts')}</NoPosts>
				)}
				{!caseStudies?.length && (
					<NoPosts>{t('caseStudies.overview.noResults')}</NoPosts>
				)}
				<LoadTrigger ref={loadTriggerRef} />
				<MarketingTestimonialCarousel />
				<PricingBrandsBanner />
				<MarketingHighlight
					title={t('caseStudies.overview.ctaBox.title')}
					body={t('caseStudies.overview.ctaBox.body')}
					cta={{
						url: '/contact-us',
						title: t('caseStudies.overview.ctaBox.ctaButton.title'),
					}}
					darkBackground={true}
				/>
			</main>
		</Fragment>
	);
};

export default CaseStudiesOverview;
